import React, { useContext, useEffect, useState } from 'react';
import { Heading } from '@components/atoms/heading/Heading';
import Box from '@components/atoms/box/Box';
import Button from '@components/atoms/button/Button';
import Modal from '@components/atoms/modal/Modal';
import AuthContext from '@src/context/AuthContext';
import {
  checkMergePossible,
  mergeAccounts,
} from '../../../utils/mergeAccountHelper';
import {
  authenticateWithPopup,
  getIdTokenSilently,
  getTokenSilently,
  logoutWithRedirect,
} from '@src/utils/authHelper';
import Svg from '@components/atoms/svg/Svg';
import LoaderSpinner from '@components/atoms/loaderSpinner/LoaderSpinner';
import { useMigrationModal } from '@context/MigrationModalContext';
import { useQuery } from '@apollo/client';
import { GET_PROFILE_SETTINGS_INFO } from '@src/apollo/query';

import {
  successMessage,
  confirmDifferentEmailsMessage,
  errorMessage,
} from './MergeAccountStrings';

function emailMask(email) {
  const [localPart, domain] = email.split('@');
  const maskedLocalPart =
    localPart.slice(0, 3) + '*'.repeat(localPart.length - 3);
  return maskedLocalPart + '@' + domain;
}

export const MigrationNoticeModal = context => {
  const authContext = useContext(AuthContext);
  const [userEmail, setUserEmail] = useState();

  const { data } = useQuery(GET_PROFILE_SETTINGS_INFO, {
    variables: { username: context.slug },
  });

  useEffect(() => {
    if (data) {
      setUserEmail(emailMask(data.user.email));
    }
  }, [data]);

  const {
    isModalOpen,
    closeModal,
    modalContent,
    setModalContent,
    isLoading,
    setLoading,
  } = useMigrationModal();

  const handleError = async msg => {
    setLoading(false);
    if (msg.can_be_merged && !msg.same_account) {
      const userData = await getIdTokenSilently();
      setModalContent(
        confirmDifferentEmailsMessage(
          userEmail,
          emailMask(userData?.user_email)
        )
      );
    } else if (!msg.can_be_merged && !msg.same_account) {
      setModalContent(errorMessage);
    }
  };
  const handleSuccess = () => {
    setLoading(false);
    setModalContent(successMessage);
    authContext.setWillDoMigrationLater(true);
    authContext.setIsMerged(true);
  };

  const handleConfirm = async () => {
    setLoading(true);
    const token = await getTokenSilently();
    setLoading(false);
    mergeAccounts(token, handleError, handleSuccess);
  };

  const handleLogout = () => {
    logoutWithRedirect();
  };

  const handleLaterClick = () => {
    closeModal();
    localStorage.setItem('willDoMigrationLater', 1);
  };

  const handleAdskLogin = async () => {
    setLoading(true);
    const token = await authenticateWithPopup();
    if (token && token.length > 0) {
      checkMergePossible(token, handleError, handleSuccess);
    }
  };

  return (
    <>
      <Modal isShowing={isModalOpen} hide={closeModal}>
        <Box>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '8px',
            }}
          >
            {modalContent?.type === 'success' && (
              <Svg
                icon="circle-check"
                css={{
                  fill: '$green',
                  height: '$48',
                  width: '$48',
                  marginRight: '$12',
                }}
              />
            )}
            {modalContent?.type === 'info' && (
              <Svg
                icon={'info'}
                css={{
                  fill: '$red',
                  height: '$32',
                  width: '$32',
                  marginRight: '$12',
                }}
              />
            )}
            {modalContent?.type === 'warning' && (
              <Svg
                icon={'warning'}
                css={{
                  fill: '$red',
                  height: '$32',
                  width: '$32',
                  marginRight: '$12',
                }}
              />
            )}
            {modalContent?.type === 'error' && (
              <Svg
                icon={'error'}
                css={{
                  fill: '$red',
                  height: '$32',
                  width: '$32',
                  marginRight: '$12',
                }}
              />
            )}
            {modalContent.subheader && (
              <Heading level="h5">{modalContent.subheader}</Heading>
            )}
          </div>
          <Heading
            css={{
              fontFamily: '$ArtifaktLegendBold',
              fontWeight: 900,
              lineHeight: 1.2,
              marginBottom: '24px',
            }}
            level="h4"
          >
            {modalContent.title}
          </Heading>

          <Box
            css={{
              marginBottom: '$32',
            }}
          >
            {modalContent?.content}
          </Box>
          {isLoading ? (
            <div
              style={{
                margin: '8px',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <LoaderSpinner size={48} />
            </div>
          ) : (
            <div
              style={{
                marginTop: '20px',
                display: 'flex',
                gap: '12px',
              }}
            >
              {modalContent?.actions?.includes('cancel') && (
                <Button
                  type="normal"
                  colorTheme="gray"
                  label={'Cancel'}
                  actionType="onclick"
                  onClick={closeModal}
                />
              )}
              {modalContent?.actions?.includes('doLater') && (
                <Button
                  id="action-button"
                  type="normal"
                  colorTheme="outlined"
                  label={
                    !authContext.isMerged
                      ? "I'll do this later"
                      : "Ok, don't show this again"
                  }
                  actionType="onclick"
                  onClick={handleLaterClick}
                />
              )}
              {modalContent?.actions?.includes('logout') && (
                <Button
                  type="normal"
                  colorTheme="outlined"
                  actionType="onclick"
                  onClick={handleLogout}
                  svgIcon={'adsk'}
                >
                  Logout from Autodesk
                </Button>
              )}
              {modalContent?.actions?.includes('confirm') && (
                <Button actionType="onclick" onClick={handleConfirm}>
                  Confirm
                </Button>
              )}
              {modalContent?.actions?.includes('signin') && (
                <Button
                  type="normal"
                  label="Merge with Autodesk"
                  colorTheme="adsk"
                  onClick={handleAdskLogin}
                  actionType="onclick"
                  svgIcon={'adsk'}
                />
              )}
            </div>
          )}
        </Box>
      </Modal>
    </>
  );
};
